import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, Typography, Card, CardContent, Grid, Button,
  TextField, Avatar, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import LockIcon from '@mui/icons-material/Lock';
import { BACKEND_DOMAIN } from '../../../../common/env';

const AdminDashboardProfile = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmNewPassword: ''
  });
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  useEffect(() => {
    const fetchUserData = async () => {
      if (!token) {
        navigate('/login');
        return;
      }
      
      try {
        const response = await axios.get(`${BACKEND_DOMAIN}/my-details`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching user details:', error);
        if (error.response && error.response.status === 401) {
          localStorage.removeItem('token');
          navigate('/login');
        }
        setLoading(false);
      }
    };

    fetchUserData();
  }, [token, navigate]);

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
  };

  const handleSavePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmNewPassword) {
      alert('New passwords do not match!');
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      alert('Password must be at least 6 characters long!');
      return;
    }

    try {
      await axios.put(`${BACKEND_DOMAIN}/change-password`, 
        { newPassword: passwordData.newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert('Password updated successfully!');
      setShowPasswordDialog(false);
      setPasswordData({ newPassword: '', confirmNewPassword: '' });
    } catch (error) {
      console.error('Error updating password:', error);
      if (error.response && error.response.status === 401) {
        alert('Your session has expired. Please log in again.');
        localStorage.removeItem('token');
        navigate('/login');
      } else {
        alert('Failed to update password. Please try again.');
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <Typography>Loading profile...</Typography>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <Typography>Error loading profile. Please try again.</Typography>
      </Box>
    );
  }

  // Ensure user object has required properties
  const userData = {
    username: user.username || '',
    email: user.email || '',
    role: user.role || 'Admin',
    createdAt: user.createdAt || null,
    avatar: user.avatar || ''
  };

  return (
    <Box className="main-content" sx={{ flex: 1, p: { xs: 2, sm: 3 } }}>
      <Typography variant="h4" component="h1" sx={{ mb: 3, textAlign: { xs: 'center', sm: 'left' } }}>
        My Profile
      </Typography>

      <Box sx={{ maxWidth: { xs: '100%', sm: '600px' }, mx: { xs: 'auto', sm: 0 } }}>
        <Card sx={{ 
          boxShadow: 3, 
          borderRadius: 2,
          transition: 'box-shadow 0.3s ease-in-out',
          '&:hover': {
            boxShadow: 6
          }
        }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' }, 
              gap: { xs: 2, sm: 3 }, 
              mb: 3,
              alignItems: 'center',
              textAlign: 'center'
            }}>
              <Avatar 
                sx={{ 
                  width: { xs: 80, sm: 100 }, 
                  height: { xs: 80, sm: 100 }, 
                  fontSize: { xs: 32, sm: 40 }, 
                  mb: { xs: 1, sm: 0 }
                }}
                src={userData.avatar}
              >
                {userData.username ? userData.username.charAt(0).toUpperCase() : 'U'}
              </Avatar>
              
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {userData.username || 'User'}
                </Typography>
                <Typography variant="body1" color="textSecondary">
                  {userData.role}
                </Typography>
              </Box>
            </Box>
            
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Username"
                  name="username"
                  value={userData.username}
                  disabled
                  variant="outlined"
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(0, 0, 0, 0.02)'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  value={userData.email}
                  disabled
                  variant="outlined"
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(0, 0, 0, 0.02)'
                    }
                  }}
                />
              </Grid>
            </Grid>
            
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' }, 
              gap: 2,
              alignItems: { xs: 'stretch', sm: 'center' },
              justifyContent: 'space-between'
            }}>
              <Box>
                <Typography variant="body2" color="textSecondary">
                  Role: {userData.role}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Member since: {userData.createdAt ? new Date(userData.createdAt).toLocaleDateString() : 'N/A'}
                </Typography>
              </Box>
              
              <Button
                variant="contained"
                startIcon={<LockIcon />}
                onClick={() => setShowPasswordDialog(true)}
                sx={{ 
                  minWidth: { xs: '100%', sm: 'auto' },
                  py: 1.5,
                  px: 3
                }}
              >
                Change Password
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Change Password Dialog */}
      <Dialog open={showPasswordDialog} onClose={() => setShowPasswordDialog(false)}>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="newPassword"
            label="New Password"
            type="password"
            fullWidth
            variant="outlined"
            value={passwordData.newPassword}
            onChange={handlePasswordChange}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="confirmNewPassword"
            label="Retype New Password"
            type="password"
            fullWidth
            variant="outlined"
            value={passwordData.confirmNewPassword}
            onChange={handlePasswordChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPasswordDialog(false)}>Cancel</Button>
          <Button onClick={handleSavePassword} variant="contained">Save Password</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminDashboardProfile;